import type {
  ProjectDto,
  ProjectActivityDto,
  ProjectCreateInput,
  ProjectUpdateInput,
  UsageDashboardDto,
  ProjectChatDto,
  ProjectChatAttachmentDto,
  ProjectChatSendResponse,
  ProjectChatSettingsUpdateInput,
  ProjectChatMessageSource,
  RenovationMilestoneDto,
  RenovationMilestoneCreateInput,
  RenovationMilestoneUpdateInput,
} from '@cc/types';

export interface ApiUser {
  id: string;
  email: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body.error ?? 'http_error',
      body.message ?? res.statusText,
      body.details,
    );
  }
  return body as T;
}

export const api = {
  // auth
  requestMagicLink: (email: string) =>
    request<void>('/api/auth/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  callback: (token: string) =>
    request<{ user: ApiUser }>('/api/auth/callback', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  me: () => request<{ user: ApiUser }>('/api/auth/me'),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  // platform
  health: () => request<{ status: string; db: string }>('/api/health'),

  // projects
  listProjects: (params?: { status?: string }) => {
    const q = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return request<{ projects: ProjectDto[] }>(`/api/projects${q}`);
  },
  getProject: (id: string) =>
    request<{ project: ProjectDto; activities: ProjectActivityDto[] }>(
      `/api/projects/${id}`,
    ),
  createProject: (input: ProjectCreateInput) =>
    request<{ project: ProjectDto }>(`/api/projects`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateProject: (id: string, input: ProjectUpdateInput) =>
    request<{ project: ProjectDto }>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteProject: (id: string) =>
    request<void>(`/api/projects/${id}`, { method: 'DELETE' }),

  // settings
  getUsage: (days = 30) => request<UsageDashboardDto>(`/api/settings/usage?days=${days}`),

  // accounts (Gmail)
  listAccounts: () =>
    request<{
      accounts: Array<{
        id: string;
        label: string;
        email: string;
        oauthClientKey: string | null;
        scopes: string[];
        lastSyncAt: string | null;
        lastSyncError: string | null;
        hasHistoryId: boolean;
        messageCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('/api/accounts'),
  disconnectAccount: (id: string) =>
    request<void>(`/api/accounts/${id}`, { method: 'DELETE' }),
  listOAuthClients: () =>
    request<{
      clients: Array<{
        key: string;
        displayName: string;
        domains: string[];
        internal: boolean;
      }>;
    }>('/api/google/oauth/clients'),

  // messages
  listMessages: (params: {
    accounts?: string[];
    classifications?: string[];
    tags?: string[];
    projectId?: string;
    search?: string;
    cursor?: string;
    sort?: 'urgency' | 'recent';
  }) => {
    const q = new URLSearchParams();
    if (params.accounts?.length) q.set('account', params.accounts.join(','));
    if (params.classifications?.length) q.set('classification', params.classifications.join(','));
    if (params.tags?.length) q.set('tag', params.tags.join(','));
    if (params.projectId) q.set('project', params.projectId);
    if (params.search) q.set('search', params.search);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.sort) q.set('sort', params.sort);
    const qs = q.toString();
    return request<import('@cc/types').MessagesListDto>(`/api/messages${qs ? `?${qs}` : ''}`);
  },
  getMessage: (id: string) =>
    request<{ message: import('@cc/types').MessageDetailDto }>(`/api/messages/${id}`),
  getMessageContext: (id: string) =>
    request<import('@cc/types').MessageContextDto>(`/api/messages/${id}/context`),
  getThreadSummary: (id: string) =>
    request<import('@cc/types').ThreadSummaryDto>(`/api/messages/${id}/thread-summary`),
  snoozeMessage: (id: string, input: { hours?: number; until?: string }) =>
    request<{ id: string; snoozedUntil: string }>(`/api/messages/${id}/snooze`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  dismissMessage: (id: string) =>
    request<{ id: string; dismissedAt: string }>(`/api/messages/${id}/dismiss`, {
      method: 'POST',
    }),
  unhideMessage: (id: string) =>
    request<{ id: string }>(`/api/messages/${id}/unhide`, { method: 'POST' }),

  // drafts
  listDrafts: (status: 'pending' | 'terminal' | 'all' = 'pending') =>
    request<{ drafts: import('@cc/types').DraftDto[] }>(`/api/drafts?status=${status}`),
  getDraft: (id: string) =>
    request<{ draft: import('@cc/types').DraftDto }>(`/api/drafts/${id}`),
  createDraft: (input: import('@cc/types').DraftCreateInput) =>
    request<{ draft: import('@cc/types').DraftDto; existed?: boolean }>(`/api/drafts`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateDraft: (id: string, input: import('@cc/types').DraftUpdateInput | string) =>
    request<{ draft: import('@cc/types').DraftDto }>(`/api/drafts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(typeof input === 'string' ? { body: input } : input),
    }),
  approveDraft: (id: string) =>
    request<{ draft: import('@cc/types').DraftDto }>(`/api/drafts/${id}/approve`, {
      method: 'POST',
    }),
  regenerateDraft: (
    id: string,
    input: import('@cc/types').DraftRegenerateInput = {},
  ) =>
    request<{ draft: import('@cc/types').DraftDto | null }>(`/api/drafts/${id}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  suggestDraftIntents: (id: string) =>
    request<import('@cc/types').DraftIntentsDto>(`/api/drafts/${id}/suggest-intents`, {
      method: 'POST',
    }),
  uploadDraftAttachment: (
    id: string,
    input: { filename: string; mimeType: string; contentBase64: string; sourceMessageId?: string },
  ) =>
    request<{ attachment: import('@cc/types').DraftAttachmentDto }>(
      `/api/drafts/${id}/attachments`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  deleteDraftAttachment: (id: string, attachId: string) =>
    request<void>(`/api/drafts/${id}/attachments/${attachId}`, { method: 'DELETE' }),
  draftAttachmentUrl: (id: string, attachId: string) =>
    `/api/drafts/${id}/attachments/${attachId}`,
  messageAttachmentUrl: (id: string, gmailAttachmentId: string) =>
    `/api/messages/${id}/attachments/${encodeURIComponent(gmailAttachmentId)}`,
  rateDraft: (id: string, styleRating: import('@cc/types').DraftStyleRating | null) =>
    request<{ draft: import('@cc/types').DraftDto }>(`/api/drafts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ styleRating }),
    }),
  discardDraft: (id: string) =>
    request<void>(`/api/drafts/${id}/discard`, { method: 'POST' }),

  // renovation
  getRenovation: (projectId: string) =>
    request<import('@cc/types').RenovationHubDto>(
      `/api/projects/${projectId}/renovation`,
    ),
  createRoom: (projectId: string, input: { name: string; notes?: string }) =>
    request<{ room: import('@cc/types').RenovationRoomDto }>(
      `/api/projects/${projectId}/renovation/rooms`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  updateRoom: (
    projectId: string,
    roomId: string,
    input: Partial<{
      name: string;
      notes: string | null;
      sortOrder: number;
      heroMediaId: string | null;
    }>,
  ) =>
    request<{ room: import('@cc/types').RenovationRoomDto }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  deleteRoom: (projectId: string, roomId: string) =>
    request<void>(`/api/projects/${projectId}/renovation/rooms/${roomId}`, {
      method: 'DELETE',
    }),
  createItem: (
    projectId: string,
    input: { roomId: string; activity: string; status?: string },
  ) =>
    request<{ item: import('@cc/types').RenovationItemDto }>(
      `/api/projects/${projectId}/renovation/items`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  updateItem: (
    projectId: string,
    itemId: string,
    input: Partial<import('@cc/types').RenovationItemDto>,
  ) =>
    request<{ item: import('@cc/types').RenovationItemDto }>(
      `/api/projects/${projectId}/renovation/items/${itemId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  deleteItem: (projectId: string, itemId: string) =>
    request<void>(`/api/projects/${projectId}/renovation/items/${itemId}`, {
      method: 'DELETE',
    }),
  createExpense: (
    projectId: string,
    input: {
      amount: number;
      date: string;
      vendor?: string | null;
      description?: string | null;
      roomId?: string | null;
      itemId?: string | null;
    },
  ) =>
    request<{ expense: import('@cc/types').RenovationExpenseDto }>(
      `/api/projects/${projectId}/renovation/expenses`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  deleteExpense: (projectId: string, expenseId: string) =>
    request<void>(`/api/projects/${projectId}/renovation/expenses/${expenseId}`, {
      method: 'DELETE',
    }),

  renoChat: (
    projectId: string,
    input: {
      message: string;
      sessionId?: string;
      attachments?: Array<{ filename: string; mimeType: string; contentBase64: string }>;
    },
  ) =>
    request<{
      sessionId: string;
      reply: string;
      toolCalls: Array<{ name: string; input: unknown; result: unknown }>;
    }>(`/api/projects/${projectId}/renovation/chat`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // briefs
  listBriefs: () =>
    request<{
      briefs: Array<{
        id: string;
        type: string;
        status: string;
        scheduledFor: string;
        generatedAt: string | null;
        createdAt: string;
      }>;
    }>('/api/briefs'),
  getBrief: (id: string) =>
    request<{
      brief: {
        id: string;
        type: string;
        status: string;
        scheduledFor: string;
        generatedAt: string | null;
        contentMd: string | null;
        inputsMeta: unknown;
        error: string | null;
        deliveredTo: string[];
        createdAt: string;
      };
    }>(`/api/briefs/${id}`),
  generateBriefNow: () =>
    request<{ status: string }>(`/api/briefs/generate-now`, { method: 'POST' }),

  // calendar (Phase A)
  listCalendarEvents: (params?: { from?: string; to?: string; accounts?: string[] }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.accounts?.length) q.set('accounts', params.accounts.join(','));
    const qs = q.toString();
    return request<import('@cc/types').CalendarEventsListDto>(
      `/api/calendar/events${qs ? `?${qs}` : ''}`,
    );
  },
  listCalendarAccountStatuses: () =>
    request<import('@cc/types').CalendarAccountStatusListDto>(
      '/api/calendar/accounts/status',
    ),
  listAccountCalendars: (accountId: string) =>
    request<import('@cc/types').AccountCalendarsListDto>(
      `/api/calendar/accounts/${accountId}/calendars`,
    ),
  setAccountCalendarSync: (accountId: string, googleCalendarId: string, syncEnabled: boolean) =>
    request<{ ok: true }>(
      `/api/calendar/accounts/${accountId}/calendars/${encodeURIComponent(googleCalendarId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ syncEnabled }),
      },
    ),
  createCalendarEvent: (input: import('@cc/types').CalendarCreateInput) =>
    request<{ event: import('@cc/types').CalendarEventDto }>(`/api/calendar/events`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateCalendarEvent: (id: string, input: import('@cc/types').CalendarUpdateInput) =>
    request<{ event: import('@cc/types').CalendarEventDto }>(`/api/calendar/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteCalendarEvent: (
    id: string,
    notify = false,
    scope: 'this' | 'following' | 'all' = 'this',
  ) => {
    const q = new URLSearchParams();
    if (notify) q.set('notify', 'true');
    if (scope !== 'this') q.set('scope', scope);
    const qs = q.toString();
    return request<void>(`/api/calendar/events/${id}${qs ? `?${qs}` : ''}`, {
      method: 'DELETE',
    });
  },
  rsvpCalendarEvent: (id: string, response: import('@cc/types').CalendarRsvpResponse) =>
    request<{ event: import('@cc/types').CalendarEventDto }>(`/api/calendar/events/${id}/rsvp`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),
  parseEventText: (text: string, accountId?: string) =>
    request<import('@cc/types').CalendarParseResultDto>(`/api/calendar/parse-event`, {
      method: 'POST',
      body: JSON.stringify({ text, accountId }),
    }),

  // renovation media (Phase J)
  listRoomMedia: (projectId: string, roomId: string, kind?: import('@cc/types').RenovationMediaKind) => {
    const q = kind ? `?kind=${kind}` : '';
    return request<import('@cc/types').RenovationMediaListDto>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media${q}`,
    );
  },
  uploadRoomMedia: (
    projectId: string,
    roomId: string,
    input: import('@cc/types').RenovationMediaUploadInput,
  ) =>
    request<{ media: import('@cc/types').RenovationMediaDto }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  updateRoomMedia: (
    projectId: string,
    roomId: string,
    mediaId: string,
    input: import('@cc/types').RenovationMediaUpdateInput,
  ) =>
    request<{ media: import('@cc/types').RenovationMediaDto }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  deleteRoomMedia: (projectId: string, roomId: string, mediaId: string) =>
    request<void>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}`,
      { method: 'DELETE' },
    ),
  refreshMediaUrl: (projectId: string, mediaId: string) =>
    request<{ url: string; expiresAt: string }>(
      `/api/projects/${projectId}/renovation/media/${mediaId}/url`,
    ),

  // --- media pins (Frame.io-style annotations on the room hero) ---
  listMediaPins: (projectId: string, roomId: string, mediaId: string) =>
    request<{ pins: import('@cc/types').RenovationMediaPinDto[] }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}/pins`,
    ),
  createMediaPin: (
    projectId: string,
    roomId: string,
    mediaId: string,
    input: {
      xNorm: number;
      yNorm: number;
      comment: string;
      linkedItemId?: string | null;
      category?: import('@cc/types').RenovationPinCategory;
      status?: import('@cc/types').RenovationPinStatus;
      dueDate?: string | null;
      costImpact?: number | null;
      assignee?: string | null;
    },
  ) =>
    request<{ pin: import('@cc/types').RenovationMediaPinDto }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}/pins`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  updateMediaPin: (
    projectId: string,
    roomId: string,
    mediaId: string,
    pinId: string,
    patch: Partial<{
      xNorm: number;
      yNorm: number;
      comment: string;
      linkedItemId: string | null;
      category: import('@cc/types').RenovationPinCategory;
      status: import('@cc/types').RenovationPinStatus;
      dueDate: string | null;
      costImpact: number | null;
      assignee: string | null;
    }>,
  ) =>
    request<{ pin: import('@cc/types').RenovationMediaPinDto }>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}/pins/${pinId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  deleteMediaPin: (
    projectId: string,
    roomId: string,
    mediaId: string,
    pinId: string,
  ) =>
    request<void>(
      `/api/projects/${projectId}/renovation/rooms/${roomId}/media/${mediaId}/pins/${pinId}`,
      { method: 'DELETE' },
    ),

  // Project chat (claude.ai Projects-style per-project thread)
  getProjectChat: (projectId: string) =>
    request<ProjectChatDto>(`/api/projects/${projectId}/chat`),
  sendProjectChat: (
    projectId: string,
    body: { message: string; source?: ProjectChatMessageSource },
  ) =>
    request<ProjectChatSendResponse>(`/api/projects/${projectId}/chat/messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchProjectChat: (projectId: string, body: ProjectChatSettingsUpdateInput) =>
    request<{ ok: true }>(`/api/projects/${projectId}/chat`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  clearProjectChat: (projectId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/chat/messages`, {
      method: 'DELETE',
    }),
  uploadProjectChatAttachment: (
    projectId: string,
    body: { filename: string; mimeType: string; contentBase64: string },
  ) =>
    request<ProjectChatAttachmentDto>(`/api/projects/${projectId}/chat/attachments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteProjectChatAttachment: (projectId: string, attachmentId: string) =>
    request<{ ok: true }>(
      `/api/projects/${projectId}/chat/attachments/${attachmentId}`,
      { method: 'DELETE' },
    ),

  // Renovation milestones (Phase B timeline)
  listMilestones: (projectId: string) =>
    request<{ milestones: RenovationMilestoneDto[] }>(
      `/api/projects/${projectId}/renovation/milestones`,
    ),
  createMilestone: (projectId: string, body: RenovationMilestoneCreateInput) =>
    request<{ milestone: RenovationMilestoneDto }>(
      `/api/projects/${projectId}/renovation/milestones`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateMilestone: (
    projectId: string,
    milestoneId: string,
    body: RenovationMilestoneUpdateInput,
  ) =>
    request<{ milestone: RenovationMilestoneDto }>(
      `/api/projects/${projectId}/renovation/milestones/${milestoneId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteMilestone: (projectId: string, milestoneId: string) =>
    request<void>(
      `/api/projects/${projectId}/renovation/milestones/${milestoneId}`,
      { method: 'DELETE' },
    ),
};
